import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './Beacon.css';
import { beaconApi } from '../api/client';
import { BeaconModel, FlatCapability, FlatSubCapability } from '../types/beacon';
import { psBucket, spendBucket, money } from '../utils/beaconScoring';
import BeaconEditor from './BeaconEditor';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canEditPage } from '../utils/rbac';

type ViewName = 'map' | 'gap' | 'compare' | 'matrix' | 'portfolio' | 'apps' | 'owners' | 'about';
type Lens = 'plain' | 'maturity' | 'pain' | 'importance' | 'priority' | 'spend' | 'gap' | 'tier';
type Persona = 'Executive' | 'Domain VP' | 'Architect';
type Density = 'compact' | 'regular' | 'comfy';

const PERSONA_DEFAULTS: Record<Persona, { lens: Lens; view: ViewName }> = {
  Executive: { lens: 'priority', view: 'map' },
  'Domain VP': { lens: 'maturity', view: 'owners' },
  Architect: { lens: 'tier', view: 'apps' },
};

const LENSES: Array<{ id: Lens; label: string }> = [
  { id: 'plain', label: 'Default' },
  { id: 'maturity', label: 'Maturity' },
  { id: 'pain', label: 'Pain' },
  { id: 'importance', label: 'Strategic Importance' },
  { id: 'priority', label: 'Priority Score' },
  { id: 'spend', label: 'Annual Spend' },
  { id: 'gap', label: 'Gap to Target' },
  { id: 'tier', label: 'Differentiation' },
];

const TABS: Array<{ id: ViewName; label: string }> = [
  { id: 'map', label: 'Capability Map' },
  { id: 'gap', label: 'Gap Analysis' },
  { id: 'compare', label: 'Current vs Target' },
  { id: 'matrix', label: 'Value Matrix' },
  { id: 'portfolio', label: 'Investment Portfolio' },
  { id: 'apps', label: 'Applications' },
  { id: 'owners', label: 'Owners' },
  { id: 'about', label: 'About' },
];

const ownerName = (owner: string | null | undefined) => (owner || '').split('—')[0].trim();
const ownerTitle = (owner: string | null | undefined) => (owner || '').split('—')[1]?.trim() || '';
const cssVar = (name: string, value: string) => ({ [name]: value } as React.CSSProperties);

const Beacon: React.FC = () => {
  const [model, setModel] = useState<BeaconModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'beacon');

  const [view, setView] = useState<ViewName>('map');
  const [lens, setLens] = useState<Lens>('plain');
  const [persona, setPersona] = useState<Persona>('Executive');
  const [density, setDensity] = useState<Density>('regular');
  const [showSubcaps, setShowSubcaps] = useState(true);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [showPriorities, setShowPriorities] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCapId, setSelectedCapId] = useState<string | null>(null);
  const [introOpen, setIntroOpen] = useState(true);

  // Gap controls
  const [gapTier, setGapTier] = useState<'all' | 'Differentiating' | 'Core' | 'Commodity'>('all');
  const [gapMinS, setGapMinS] = useState(1);
  const [gapSort, setGapSort] = useState<'ps' | 'gap' | 'spend'>('ps');

  const reload = useCallback(
    () =>
      beaconApi
        .getModel()
        .then((res) => setModel(res.data))
        .catch((e) => setError(e?.response?.data?.message || 'Failed to load Beacon')),
    [],
  );

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, [reload]);

  const { domains = [], applications = [], initiatives = [] } = model || {};

  const appById = useMemo(
    () => Object.fromEntries(applications.map((a) => [a.id, a])),
    [applications],
  );

  const allCaps: FlatCapability[] = useMemo(
    () =>
      domains.flatMap((d, di) =>
        d.capabilities.map((c) => ({
          ...c,
          domainId: d.id,
          domain: d.name,
          domainColor: d.color,
          domainIdx: di,
        })),
      ),
    [domains],
  );

  const allSubs: FlatSubCapability[] = useMemo(
    () =>
      allCaps.flatMap((c) =>
        c.subCapabilities.map((s) => ({
          ...s,
          capId: c.id,
          capName: c.name,
          domain: c.domain,
          domainColor: c.domainColor,
        })),
      ),
    [allCaps],
  );

  const capById = useMemo(() => Object.fromEntries(allCaps.map((c) => [c.id, c])), [allCaps]);

  const term = search.trim().toLowerCase();
  const dim = useCallback(
    (text: string): React.CSSProperties => (term && !text.toLowerCase().includes(term) ? { opacity: 0.15 } : {}),
    [term],
  );

  const goView = useCallback((v: ViewName) => {
    setView(v);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const applyPersona = (p: Persona) => {
    setPersona(p);
    const def = PERSONA_DEFAULTS[p];
    setLens(def.lens);
    setView(def.view);
  };

  const openDetail = (capId: string) => setSelectedCapId(capId);
  const closeDetail = () => setSelectedCapId(null);

  const jumpToMapCard = (capId: string) => {
    closeDetail();
    setView('map');
    setTimeout(() => {
      const el = document.getElementById(`beacon-cap-${capId}`);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 130;
        window.scrollTo({ top: y, behavior: 'smooth' });
        el.classList.add('flash');
        setTimeout(() => el.classList.remove('flash'), 1600);
      }
    }, 350);
  };

  if (loading) {
    return (
      <div className="beacon-app">
        <div className="beacon-loading">Loading capability model…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="beacon-app">
        <div className="beacon-loading">{error}</div>
      </div>
    );
  }

  const rootClass = [
    'beacon-app',
    `lens-${lens}`,
    showPriorities ? 'show-priorities' : '',
    showSubcaps ? '' : 'hide-subcaps',
  ].filter(Boolean).join(' ');

  const cardPad = density === 'compact' ? '8px 10px' : density === 'comfy' ? '14px 16px' : '11px 13px';

  // ---- Map view ----
  const renderMap = () => {
    const totalSpend = allCaps.reduce((a, c) => a + c.spend, 0);
    const highGap = allCaps.filter((c) => c.gap >= 2 && c.importance >= 4).length;
    const commoditySpend = allCaps.filter((c) => c.tier === 'Commodity').reduce((a, c) => a + c.spend, 0);
    const avgMaturity = allCaps.length ? (allCaps.reduce((a, c) => a + c.maturity, 0) / allCaps.length) : 0;
    const topPriorities = [...allSubs].filter((s) => s.ps).sort((a, b) => b.ps - a.ps).slice(0, 12);

    return (
      <section className="view-map">
        <div className="lens-bar">
          <span className="label">Lens</span>
          <div className="lens-group">
            {LENSES.map((l) => (
              <label key={l.id} className={lens === l.id ? 'active' : ''} onClick={() => setLens(l.id)}>
                {l.label}
              </label>
            ))}
          </div>
          <button className={`btn ${showPriorities ? 'active' : ''}`} onClick={() => setShowPriorities((v) => !v)}>
            Key Transformation Areas
          </button>
          {lens !== 'plain' && <Legend lens={lens} />}
        </div>

        <div className="map-summary">
          <div className="summary-tile accent" onClick={() => goView('portfolio')}>
            <div className="label">Total Annual Spend</div>
            <div className="value">{money(totalSpend)}</div>
            <div className="delta">across {allCaps.length} capabilities · {applications.length} apps</div>
          </div>
          <div className="summary-tile">
            <div className="label">High-Importance Gaps</div>
            <div className="value">{highGap}</div>
            <div className="delta bad">capabilities S≥4 with 2+ maturity gap</div>
          </div>
          <div className="summary-tile">
            <div className="label">Avg Maturity</div>
            <div className="value">{avgMaturity.toFixed(1)}<span style={{ fontSize: 14, color: 'var(--muted)' }}> / 5</span></div>
            <div className="delta">target avg 3.4 — gap of {(3.4 - avgMaturity).toFixed(1)}</div>
          </div>
          <div className="summary-tile">
            <div className="label">Spend on Commodity</div>
            <div className="value">{money(commoditySpend)}</div>
            <div className="delta">{totalSpend ? ((commoditySpend / totalSpend) * 100).toFixed(0) : 0}% — candidate for outsource</div>
          </div>
        </div>

        {showPriorities && (
          <div className="top-priorities">
            <div className="top-priorities-card">
              <h2>Key Transformation Areas</h2>
              <p className="sub">Sub-capabilities with the largest Priority Score (Strategic Importance × Pain × Maturity Gap), ranked. Click any row to jump to its capability.</p>
              <ol className="top-priorities-list">
                {topPriorities.map((s) => (
                  <li key={s.id} className="tp-item" data-bucket={psBucket(s.ps)} onClick={() => { setShowPriorities(false); openDetail(s.capId); }}>
                    <div className="body">
                      <div className="name">{s.name}</div>
                      <div className="path">{s.domain} → {s.capName}</div>
                    </div>
                    <div className="scores">
                      <span className="sc">S{s.importance}</span><span className="sc">P{s.pain}</span><span className="sc">G+{s.gap}</span>
                      <span className="ps">PS {s.ps}</span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}

        <div>
          {domains.map((d) => (
            <section className="domain" key={d.id} style={cssVar('--domain-color', d.color)}>
              <header>
                <div className="head-text">
                  <h2>{d.name}</h2>
                  <p>{d.description}</p>
                </div>
                <div className="head-meta">
                  <div className="owner">{ownerName(d.owner)}</div>
                  <div>{d.capabilities.length} capabilities · {money(d.capabilities.reduce((a, c) => a + c.spend, 0))}</div>
                </div>
              </header>
              <div className="l2-grid">
                {d.capabilities.map((c) => (
                  <article
                    key={c.id}
                    id={`beacon-cap-${c.id}`}
                    className="l2-card"
                    data-maturity={c.maturity}
                    data-pain={c.pain}
                    data-importance={c.importance}
                    data-priority={psBucket(c.ps)}
                    data-spend={spendBucket(c.spend)}
                    data-gap={c.gap}
                    data-tier={c.tier}
                    style={dim(`${c.name} ${c.purpose || ''}`)}
                    title={`${c.purpose || ''}  [M${c.maturity} · P${c.pain} · S${c.importance} · Gap +${c.gap} · PS${c.ps}]`}
                    onClick={() => openDetail(c.id)}
                  >
                    <h3>
                      <span className="name">{c.name}</span>
                      <span className="chip m">M {c.maturity}</span>
                      <span className="chip p">P {c.pain}</span>
                      <span className="chip s">S {c.importance}</span>
                      <span className="chip pr">PS {c.ps}</span>
                      <span className="chip sp">{money(c.spend)}</span>
                      <span className="chip gp">+{c.gap}</span>
                      <span className="chip tier">{c.tier[0]}</span>
                    </h3>
                    <ul>
                      {c.subCapabilities.map((s) => (
                        <li
                          key={s.id}
                          data-maturity={s.maturity}
                          data-pain={s.pain}
                          data-importance={s.importance}
                          data-priority={psBucket(s.ps)}
                          title={`${s.purpose || ''}  [M${s.maturity} · P${s.pain} · S${s.importance} · PS${s.ps}]`}
                        >
                          {s.name}
                        </li>
                      ))}
                    </ul>
                    <div className="meta-row">
                      <span className={`tier-chip tier-${c.tier}`}>{c.tier}</span>
                      <span className="spend">{money(c.spend)}/yr</span>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </section>
    );
  };

  // ---- Gap view ----
  const renderGap = () => {
    let caps = [...allCaps];
    if (gapTier !== 'all') caps = caps.filter((c) => c.tier === gapTier);
    caps = caps.filter((c) => c.importance >= gapMinS);
    const sortFn = {
      ps: (a: FlatCapability, b: FlatCapability) => (b.ps || 0) - (a.ps || 0),
      gap: (a: FlatCapability, b: FlatCapability) => b.gap - a.gap || b.importance - a.importance,
      spend: (a: FlatCapability, b: FlatCapability) => b.spend - a.spend,
    }[gapSort];
    caps.sort(sortFn);

    const totalGap = allCaps.reduce((a, c) => a + c.gap, 0);
    const filteredGap = caps.reduce((a, c) => a + c.gap, 0);
    const closable = caps.filter((c) => c.gap === 1).length;
    const stretch = caps.filter((c) => c.gap >= 2).length;

    return (
      <section>
        <div className="gap-head">
          <div>
            <h2>Gap Analysis</h2>
            <p>Capabilities ranked by the distance between current and target maturity, weighted by strategic importance. Use the controls to focus on the gaps that actually matter for this cycle.</p>
          </div>
          <div className="gap-stats">
            <div className="stat"><div className="lbl">In scope</div><div className="val">{caps.length}<span className="of"> / {allCaps.length}</span></div></div>
            <div className="stat"><div className="lbl">Total gap pts</div><div className="val">{filteredGap}<span className="of"> / {totalGap}</span></div></div>
            <div className="stat"><div className="lbl">1-step away</div><div className="val">{closable}</div></div>
            <div className="stat"><div className="lbl">Multi-step gaps</div><div className="val" style={{ color: '#b8421a' }}>{stretch}</div></div>
          </div>
        </div>

        <div className="gap-controls">
          <span className="label">Filter</span>
          <div className="chip-toggles">
            {(['all', 'Differentiating', 'Core', 'Commodity'] as const).map((t) => (
              <button key={t} className={gapTier === t ? 'active' : ''} onClick={() => setGapTier(t)}>{t === 'all' ? 'All tiers' : t}</button>
            ))}
          </div>
          <span className="label" style={{ marginLeft: 14 }}>Min importance</span>
          <div className="chip-toggles">
            {[{ v: 1, l: 'Any' }, { v: 3, l: 'S ≥ 3' }, { v: 4, l: 'S ≥ 4' }].map((o) => (
              <button key={o.v} className={gapMinS === o.v ? 'active' : ''} onClick={() => setGapMinS(o.v)}>{o.l}</button>
            ))}
          </div>
          <span className="label" style={{ marginLeft: 14 }}>Sort</span>
          <div className="chip-toggles">
            {[{ v: 'ps', l: 'Priority' }, { v: 'gap', l: 'Gap size' }, { v: 'spend', l: 'Annual spend' }].map((o) => (
              <button key={o.v} className={gapSort === o.v ? 'active' : ''} onClick={() => setGapSort(o.v as any)}>{o.l}</button>
            ))}
          </div>
        </div>

        <div className="gap-list">
          {caps.length === 0 && <div className="empty">No capabilities match these filters.</div>}
          {caps.map((c, i) => {
            const psB = psBucket(c.ps);
            const inits = initiatives.filter((I) => I.capabilityIds.includes(c.id));
            return (
              <article className="gap-row" key={c.id} data-bucket={psB} style={dim(c.name)} onClick={() => openDetail(c.id)}>
                <div className="rank">{String(i + 1).padStart(2, '0')}</div>
                <div className="main">
                  <div className="hd">
                    <div className="name">{c.name}</div>
                    <span className={`tier-chip tier-${c.tier}`}>{c.tier}</span>
                  </div>
                  <div className="sub">{c.domain} · {ownerName(c.owner)} · {money(c.spend)}/yr</div>
                  {inits.length
                    ? <span className="gap-init" title={inits.map((i2) => i2.name).join(' · ')}>{inits.length} initiative{inits.length > 1 ? 's' : ''} in flight</span>
                    : <span className="gap-init none">No initiatives — unfunded</span>}
                </div>
                <div className="bar-col">
                  <div className="bar-labels"><span>M {c.maturity}</span><span className="gap-tag">{c.gap === 0 ? 'At target' : `+${c.gap} gap`}</span><span>Target {c.target}</span></div>
                  <div className="bar">
                    {[1, 2, 3, 4, 5].map((j) => <div key={j} className={`seg ${j <= c.maturity ? 'current' : j <= c.target ? 'target' : ''}`} />)}
                  </div>
                </div>
                <div className="scores">
                  <span className="sc"><span className="lbl">S</span>{c.importance}</span>
                  <span className="sc"><span className="lbl">P</span>{c.pain}</span>
                  <span className="ps">PS {c.ps}</span>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    );
  };

  // ---- Compare view ----
  const renderCompare = () => {
    const totalGap = allCaps.reduce((a, c) => a + c.gap, 0);
    const atTarget = allCaps.filter((c) => c.gap === 0).length;
    const oneStep = allCaps.filter((c) => c.gap === 1).length;
    const multi = allCaps.filter((c) => c.gap >= 2).length;
    return (
      <section>
        <div className="compare-head">
          <div>
            <h2>Current vs Target Maturity</h2>
            <p>Side-by-side view of where each capability sits today and where the model says it needs to be. Click any tile to see what's standing in the way.</p>
          </div>
          <div className="compare-summary">
            <div className="stat"><div className="lbl">At target</div><div className="val" style={{ color: 'var(--pear-deep)' }}>{atTarget}</div></div>
            <div className="stat"><div className="lbl">1 step away</div><div className="val">{oneStep}</div></div>
            <div className="stat"><div className="lbl">2+ step gaps</div><div className="val" style={{ color: '#b8421a' }}>{multi}</div></div>
            <div className="stat"><div className="lbl">Total points</div><div className="val">{totalGap}</div></div>
          </div>
        </div>
        <div className="compare-legend">
          <span className="label">Maturity</span>
          {[{ c: 'var(--m1)', l: '1 Ad hoc' }, { c: 'var(--m2)', l: '2' }, { c: 'var(--m3)', l: '3' }, { c: 'var(--m4)', l: '4' }, { c: 'var(--m5)', l: '5 Optimized' }].map((x, i) => (
            <span className="legend-row" key={i}><span className="legend-sw" style={{ background: x.c }} />{x.l}</span>
          ))}
        </div>
        <div className="compare-board">
          {domains.map((d) => {
            const dGap = d.capabilities.reduce((a, c) => a + c.gap, 0);
            const avgCur = d.capabilities.reduce((a, c) => a + c.maturity, 0) / d.capabilities.length;
            const avgTgt = d.capabilities.reduce((a, c) => a + c.target, 0) / d.capabilities.length;
            return (
              <section className="cmp-domain" key={d.id} style={cssVar('--domain-color', d.color)}>
                <header>
                  <div>
                    <h3>{d.name}</h3>
                    <div className="meta">{d.capabilities.length} capabilities · {ownerName(d.owner)}</div>
                  </div>
                  <div className="cmp-totals">
                    <span className="t"><span className="k">Avg current</span><span className="v">{avgCur.toFixed(1)}</span></span>
                    <span className="t"><span className="k">Avg target</span><span className="v">{avgTgt.toFixed(1)}</span></span>
                    <span className="t"><span className="k">Domain gap</span><span className="v" style={{ color: dGap >= 4 ? '#b8421a' : 'var(--ink)' }}>+{dGap}</span></span>
                  </div>
                </header>
                <div className="cmp-grid">
                  {d.capabilities.map((c) => {
                    const arrow = c.gap === 0 ? '✓' : '→';
                    const arrowClass = c.gap === 0 ? 'arrow at' : c.gap >= 2 ? 'arrow big' : 'arrow';
                    return (
                      <div className="cmp-cap" key={c.id} style={dim(c.name)} title={`${c.name} — current M${c.maturity}, target M${c.target}, gap +${c.gap}`} onClick={() => openDetail(c.id)}>
                        <div className="cap-name">{c.name}</div>
                        <div className={`cur m-${c.maturity}`}>M{c.maturity}</div>
                        <div className={arrowClass}>{arrow}{c.gap > 0 && <span className="g">+{c.gap}</span>}</div>
                        <div className={`tgt m-${c.target}`}>M{c.target}</div>
                        <span className={`tier-chip tier-${c.tier}`}>{c.tier[0]}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </section>
    );
  };

  // ---- Matrix view ----
  const renderMatrix = () => {
    const groups: Record<string, FlatCapability[]> = {};
    allCaps.forEach((c) => {
      const k = `${c.maturity}|${c.importance}`;
      (groups[k] = groups[k] || []).push(c);
    });
    const bubbles: React.ReactNode[] = [];
    Object.entries(groups).forEach(([k, list]) => {
      const [m, s] = k.split('|').map(Number);
      list.forEach((c, idx) => {
        const x = ((m - 1) / 4) * 100;
        const y = ((s - 1) / 4) * 100;
        const size = 18 + Math.sqrt(c.spend) * 8;
        const offset = (idx % 4) * 4 - 6 * (list.length > 1 ? 1 : 0);
        const offsetY = Math.floor(idx / 4) * 4;
        bubbles.push(
          <div
            key={c.id}
            className={`bubble tier-${c.tier}`}
            style={{ left: `calc(${x}% + ${offset}px)`, bottom: `calc(${y}% + ${offsetY}px)`, width: size, height: size, ...dim(c.name) }}
            title={`${c.name} — M${c.maturity} S${c.importance} · ${money(c.spend)} · ${c.tier}`}
            onClick={() => openDetail(c.id)}
          >
            {c.spend > 6 ? c.name.split(' ').map((w) => w[0]).slice(0, 2).join('') : ''}
          </div>,
        );
      });
    });
    return (
      <section>
        <div className="matrix-head">
          <div>
            <h2>Strategic Value Matrix</h2>
            <p>Each capability plotted by Strategic Importance (Y) and Current Maturity (X). Bubble size = annual spend, color = differentiation tier. The top-right quadrant is where we're already winning; top-left is where to invest next.</p>
          </div>
          <div className="matrix-legend">
            <span className="row"><span className="sw" style={{ background: 'rgba(179,219,49,0.55)', border: '2px solid var(--pear-deep)' }} />Differentiating</span>
            <span className="row"><span className="sw" style={{ background: 'rgba(66,146,198,0.45)', border: '2px solid #1d4677' }} />Core</span>
            <span className="row"><span className="sw" style={{ background: 'rgba(0,0,0,0.08)', border: '2px solid var(--faint)' }} />Commodity</span>
          </div>
        </div>
        <div className="matrix-board">
          <span className="y-label">Strategic Importance</span>
          <span className="x-label">Current Maturity →</span>
          <div className="matrix">
            <div className="qmid h" /><div className="qmid v" />
            <div className="quadrant-label ql-tl">Invest →</div>
            <div className="quadrant-label ql-tr">Defend &amp; Extend</div>
            <div className="quadrant-label ql-bl">Tolerate</div>
            <div className="quadrant-label ql-br">Sunset / Outsource</div>
            {bubbles}
          </div>
        </div>
      </section>
    );
  };

  // ---- Portfolio view ----
  const renderPortfolio = () => {
    const horizons = ['Now', 'Next', 'Later'];
    const totalCost = initiatives.reduce((a, i) => a + i.cost, 0);
    return (
      <section>
        <div className="portfolio-head">
          <div>
            <h2>Investment Portfolio</h2>
            <p>Active and proposed capability investments grouped by horizon. Click an initiative to see which capabilities it advances.</p>
          </div>
          <div className="portfolio-stats">
            <div className="stat"><div className="lbl">In Flight</div><div className="val">{initiatives.filter((i) => i.status === 'In flight').length}</div></div>
            <div className="stat"><div className="lbl">Approved</div><div className="val">{initiatives.filter((i) => i.status === 'Approved' || i.status === 'Scaling').length}</div></div>
            <div className="stat"><div className="lbl">Proposed</div><div className="val">{initiatives.filter((i) => i.status === 'Proposed').length}</div></div>
            <div className="stat"><div className="lbl">Total Investment</div><div className="val">{money(totalCost)}</div></div>
          </div>
        </div>
        <div className="horizon-grid">
          {horizons.map((h) => {
            const items = initiatives.filter((i) => i.horizon === h);
            const hc = items.reduce((a, i) => a + i.cost, 0);
            return (
              <div className="horizon-col" data-horizon={h} key={h}>
                <header>
                  <h3>{h}</h3>
                  <span className="count">{items.length} initiatives · {money(hc)}</span>
                </header>
                {items.map((i) => (
                  <div className="init-card" key={i.id} style={dim(i.name)} onClick={() => i.capabilityIds[0] && openDetail(i.capabilityIds[0])}>
                    <div className="name">{i.name}</div>
                    <div className="tags">
                      <span className={`pill pill-status status-${i.status.toLowerCase().replace(/\s+/g, '-')}`}>{i.status}</span>
                      <span className={`pill pill-conf conf-${i.confidence}`}><i className="dot" />{i.confidence} confidence</span>
                    </div>
                    <div className="meta"><span>{i.owner}</span><span className="sep">·</span><span>{money(i.cost)}</span></div>
                    <div className="outcome"><span className="lbl">Outcome:</span> {i.outcome}</div>
                    <div className="advances">Advances: {i.capabilityIds.map((cId) => capById[cId]?.name).filter(Boolean).join(' · ')}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  // ---- Applications view ----
  const renderApps = () => {
    const buckets = ['strategic', 'invest', 'tolerate', 'retire'];
    const capsByApp: Record<string, FlatCapability[]> = {};
    allCaps.forEach((c) => c.applicationIds.forEach((a) => { (capsByApp[a] = capsByApp[a] || []).push(c); }));
    const sorted = [...applications].sort((a, b) => b.cost - a.cost);
    return (
      <section>
        <div className="apps-head">
          <div>
            <h2>Application Landscape</h2>
            <p>Systems supporting the capability model, with TIME-style classification (Strategic / Invest / Tolerate / Retire).</p>
          </div>
        </div>
        <div className="apps-bucket-summary">
          {buckets.map((b) => {
            const list = applications.filter((a) => a.status === b);
            const cost = list.reduce((a, x) => a + x.cost, 0);
            return (
              <div className={`b ${b}`} key={b}>
                <div className="lbl">{b}</div>
                <div className="val">{list.length}</div>
                <div className="sub">{money(cost)} / yr</div>
              </div>
            );
          })}
        </div>
        <table className="apps-table">
          <thead>
            <tr><th>Application</th><th>Type</th><th>Status</th><th className="num">Annual Cost</th><th className="num">Capabilities</th></tr>
          </thead>
          <tbody>
            {sorted.map((a) => {
              const caps = capsByApp[a.id] || [];
              return (
                <tr key={a.id} style={dim(`${a.name} ${a.vendor || ''}`)}>
                  <td><div className="app-name">{a.name}</div><div className="vendor">{a.vendor}</div></td>
                  <td>{a.type}</td>
                  <td><span className={`status status-${a.status}`}>{a.status}</span></td>
                  <td className="num"><strong>{money(a.cost)}</strong></td>
                  <td className="num"><span className="cap-count" title={caps.map((c) => c.name).join(', ')}>{caps.length}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    );
  };

  // ---- Owners view ----
  const renderOwners = () => {
    const sorted = [...domains].sort((a, b) =>
      b.capabilities.reduce((x, c) => x + c.spend, 0) - a.capabilities.reduce((x, c) => x + c.spend, 0));
    return (
      <section>
        <div className="owners-head">
          <div>
            <h2>Capabilities by Owner</h2>
            <p>Domain VPs accountable for capability performance. Spend, gap, and active initiatives roll up to each owner.</p>
          </div>
        </div>
        {sorted.map((d) => {
          const dSpend = d.capabilities.reduce((a, c) => a + c.spend, 0);
          const dGap = d.capabilities.reduce((a, c) => a + c.gap, 0);
          const dInits = initiatives.filter((i) => i.capabilityIds.some((cid) => d.capabilities.find((c) => c.id === cid))).length;
          const diff = d.capabilities.filter((c) => c.tier === 'Differentiating').length;
          const caps = [...d.capabilities].sort((a, b) => (b.ps || 0) - (a.ps || 0));
          return (
            <div className="owner-card" key={d.id} style={cssVar('--domain-color', d.color)}>
              <header>
                <div>
                  <div className="ow-name">{ownerName(d.owner)}</div>
                  <div className="ow-domain">{ownerTitle(d.owner)} · {d.name}</div>
                </div>
                <div className="ow-stats">
                  <div><span className="lbl">Capabilities</span><span className="v">{d.capabilities.length}</span></div>
                  <div><span className="lbl">Annual Spend</span><span className="v">{money(dSpend)}</span></div>
                  <div><span className="lbl">Differentiating</span><span className="v">{diff}</span></div>
                  <div><span className="lbl">Total Gap</span><span className="v">+{dGap}</span></div>
                  <div><span className="lbl">Initiatives</span><span className="v">{dInits}</span></div>
                </div>
              </header>
              <div className="owner-cap-table">
                <div>Capability</div><div>Tier</div><div className="num">M / Target</div><div className="num">P</div><div className="num">PS</div><div className="num">Spend</div>
                {caps.map((c) => (
                  <React.Fragment key={c.id}>
                    <div className="nm" style={dim(c.name)} onClick={() => openDetail(c.id)}>{c.name}</div>
                    <div><span className={`tier-chip tier-${c.tier}`} style={{ fontSize: 9, padding: '1px 6px' }}>{c.tier[0]}</span></div>
                    <div className="num">{c.maturity} → {c.target}</div>
                    <div className="num">{c.pain}</div>
                    <div className="num" style={{ fontWeight: 600, color: psBucket(c.ps) >= 4 ? '#b8421a' : 'var(--ink)' }}>{c.ps}</div>
                    <div className="num">{money(c.spend)}</div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </section>
    );
  };

  // ---- About view ----
  const renderAbout = () => (
    <section>
      <div className="intro-banner" style={{ borderLeftColor: '#4292c6' }}>
        <div className="ib-icon" style={{ background: '#e0ecf6' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1d4677" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v5l3 2" /></svg>
        </div>
        <div>
          <h3>About Beacon</h3>
          <p>Beacon is the Business Capability Model — a single source of truth for what the organization does, how well it does it today, and where to invest next. It serves three audiences: the <strong>Executive Leadership Team</strong> for long-range planning, <strong>Domain VPs</strong> for annual planning in their portfolio, and <strong>Enterprise Architecture</strong> to rationalize the application landscape against the capabilities those apps support.</p>
          <p style={{ marginTop: 10 }}>Start in <strong>Capability Map</strong> for the overview, jump to <strong>Gap Analysis</strong> for the prioritized to-do list, and use <strong>Current vs Target</strong> to anchor where you're going.</p>
        </div>
      </div>
      <div className="card about-card">
        <h3>Scoring model</h3>
        <ul>
          <li><strong>Maturity (M)</strong> 1 Ad hoc → 5 Optimized — current performance today</li>
          <li><strong>Pain (P)</strong> 1 None → 5 Severe — how much it hurts right now</li>
          <li><strong>Strategic Importance (S)</strong> 1 Hygiene → 5 Mission — how central to strategy</li>
          <li><strong>Target Maturity</strong> derived: target = current + 2 if S≥4 and M≤2, else current + 1, capped at 5</li>
          <li><strong>Gap</strong> = Target − Current</li>
          <li><strong>Priority Score (PS)</strong> = S × P × (5 − M)</li>
          <li><strong>Differentiation tier</strong> Differentiating · Core · Commodity — drives Invest / Maintain / Outsource</li>
          <li><strong>Annual Spend</strong> rolled up from supporting applications + people overhead (illustrative)</li>
        </ul>
      </div>
    </section>
  );

  const selectedCap = selectedCapId ? capById[selectedCapId] : null;

  return (
    <div className={rootClass} style={cssVar('--card-padding', cardPad)}>
      <header className="topbar">
        <div className="row1">
          <div className="brand">
            <h1>Beacon Command</h1>
            <div className="subhead">Business Capability Modeling Suite</div>
            <div className="counts">{domains.length} domains · {allCaps.length} capabilities · {allSubs.length} sub-capabilities</div>
          </div>
          <div className="right">
            <div className="search">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="7" cy="7" r="5" /><path d="M11 11l4 4" /></svg>
              <input placeholder="Search capabilities, apps, owners…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Escape') setSearch(''); }} />
            </div>
            {canEdit && (
              <button className="btn" onClick={() => setEditorOpen(true)} title="Add or edit Beacon data">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                Edit data
              </button>
            )}
            <div style={{ position: 'relative' }}>
              <button className="persona-pill" onClick={() => setPersonaOpen((v) => !v)} title="Switch persona">
                <span className="dot" />
                <span>Viewing as <span className="name">{persona}</span></span>
                <span aria-hidden style={{ marginLeft: 6, opacity: 0.6 }}>⚙</span>
              </button>
              {personaOpen && (
                <PersonaPanel
                  persona={persona} density={density} showSubcaps={showSubcaps} lens={lens}
                  onPersona={applyPersona} onLens={setLens} onDensity={setDensity}
                  onShowSubcaps={setShowSubcaps} onClose={() => setPersonaOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
        <nav className="tabs">
          {TABS.map((t) => (
            <button key={t.id} className={view === t.id ? 'active' : ''} onClick={() => goView(t.id)}>
              {t.label}
              {t.id === 'portfolio' && <span className="count">{initiatives.length}</span>}
              {t.id === 'apps' && <span className="count">{applications.length}</span>}
            </button>
          ))}
        </nav>
      </header>

      <div className="beacon-main">
        {introOpen && view === 'map' && (
          <div className="intro-banner">
            <div className="ib-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2"><path d="M12 2v6M12 22v-6M2 12h6M22 12h-6M5 5l4 4M19 19l-4-4M19 5l-4 4M5 19l4-4" /></svg>
            </div>
            <div>
              <h3>Welcome to Beacon Command</h3>
              <p>A Business Capability Model. Pick a lens to color the map by Maturity, Pain, Strategic Importance, Priority, Spend, Gap to target, or Differentiation tier. Click any capability tile for full detail — owners, applications, sub-capabilities, active initiatives, and recommended decision. Switch persona (top-right) to change the default view, lens, and density for Executive · Domain VP · Architect.</p>
            </div>
            <button className="ib-close" onClick={() => setIntroOpen(false)}>×</button>
          </div>
        )}

        {view === 'map' && renderMap()}
        {view === 'gap' && renderGap()}
        {view === 'compare' && renderCompare()}
        {view === 'matrix' && renderMatrix()}
        {view === 'portfolio' && renderPortfolio()}
        {view === 'apps' && renderApps()}
        {view === 'owners' && renderOwners()}
        {view === 'about' && renderAbout()}
      </div>

      {/* Detail panel */}
      <div className={`detail-backdrop ${selectedCap ? 'open' : ''}`} onClick={closeDetail} />
      <aside className={`detail-panel ${selectedCap ? 'open' : ''}`}>
        {selectedCap && (
          <DetailPanel
            cap={selectedCap}
            apps={selectedCap.applicationIds.map((a) => appById[a]).filter(Boolean)}
            inits={initiatives.filter((i) => i.capabilityIds.includes(selectedCap.id))}
            onClose={closeDetail}
            onJumpMap={() => jumpToMapCard(selectedCap.id)}
            onJump={(v) => { closeDetail(); goView(v); }}
          />
        )}
      </aside>

      {/* Authoring panel */}
      {canEdit && model && (
        <BeaconEditor
          open={editorOpen}
          model={model}
          onClose={() => setEditorOpen(false)}
          onChanged={reload}
        />
      )}
    </div>
  );
};

// ---- Legend ----
const Legend: React.FC<{ lens: Lens }> = ({ lens }) => {
  const rows: Record<Lens, { label: string; items: Array<{ c: string; l: string }> }> = {
    plain: { label: '', items: [] },
    maturity: { label: 'Maturity', items: [{ c: 'var(--m1)', l: '1 Ad hoc' }, { c: 'var(--m2)', l: '2' }, { c: 'var(--m3)', l: '3' }, { c: 'var(--m4)', l: '4' }, { c: 'var(--m5)', l: '5 Optimized' }] },
    pain: { label: 'Pain', items: [{ c: 'var(--p1)', l: '1 None' }, { c: 'var(--p2)', l: '2' }, { c: 'var(--p3)', l: '3' }, { c: 'var(--p4)', l: '4' }, { c: 'var(--p5)', l: '5 Severe' }] },
    importance: { label: 'Strategic Importance', items: [{ c: 'var(--s1)', l: '1 Hygiene' }, { c: 'var(--s2)', l: '2' }, { c: 'var(--s3)', l: '3' }, { c: 'var(--s4)', l: '4' }, { c: 'var(--s5)', l: '5 Mission' }] },
    priority: { label: 'Priority (S×P×Gap)', items: [{ c: 'var(--ps1)', l: 'Low' }, { c: 'var(--ps2)', l: '' }, { c: 'var(--ps3)', l: '' }, { c: 'var(--ps4)', l: '' }, { c: 'var(--ps5)', l: 'Critical' }] },
    spend: { label: 'Annual Spend', items: [{ c: '#f4f7ec', l: '< $2M' }, { c: '#e6efcf', l: '$2–4M' }, { c: '#cae46d', l: '$4–7M' }, { c: '#95B925', l: '$7–12M' }, { c: '#5d7a14', l: '$12M+' }] },
    gap: { label: 'Gap to Target', items: [{ c: 'var(--m5)', l: 'At target' }, { c: 'var(--m4)', l: '+1' }, { c: 'var(--m3)', l: '+2' }, { c: 'var(--ps4)', l: '+3' }, { c: 'var(--ps5)', l: '+4' }] },
    tier: { label: 'Differentiation', items: [{ c: 'var(--pear-soft)', l: 'Differentiating' }, { c: '#e6efcf', l: 'Core' }, { c: 'var(--hair-2)', l: 'Commodity' }] },
  };
  const r = rows[lens];
  if (!r.items.length) return null;
  return (
    <div className="legend">
      <span className="label">{r.label}</span>
      {r.items.map((it, i) => (
        <span className="legend-row" key={i}><span className="legend-sw" style={{ background: it.c }} />{it.l}</span>
      ))}
    </div>
  );
};

// ---- Persona panel ----
const PersonaPanel: React.FC<{
  persona: Persona; density: Density; showSubcaps: boolean; lens: Lens;
  onPersona: (p: Persona) => void; onLens: (l: Lens) => void; onDensity: (d: Density) => void;
  onShowSubcaps: (v: boolean) => void; onClose: () => void;
}> = ({ persona, density, showSubcaps, lens, onPersona, onLens, onDensity, onShowSubcaps, onClose }) => (
  <>
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }} onClick={onClose} />
    <div style={{
      position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 61, width: 240,
      background: '#fff', border: '1px solid var(--hair)', borderRadius: 10, boxShadow: 'var(--shadow-panel)',
      padding: 14, color: 'var(--ink)',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Persona</div>
      <div className="chip-toggles" style={{ display: 'flex', marginBottom: 12 }}>
        {(['Executive', 'Domain VP', 'Architect'] as Persona[]).map((p) => (
          <button key={p} className={persona === p ? 'active' : ''} style={{ flex: 1 }} onClick={() => onPersona(p)}>{p}</button>
        ))}
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Default lens</div>
      <select className="btn" style={{ width: '100%', marginBottom: 12 }} value={lens} onChange={(e) => onLens(e.target.value as Lens)}>
        {LENSES.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
      </select>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Density</div>
      <div className="chip-toggles" style={{ display: 'flex', marginBottom: 12 }}>
        {(['compact', 'regular', 'comfy'] as Density[]).map((d) => (
          <button key={d} className={density === d ? 'active' : ''} style={{ flex: 1 }} onClick={() => onDensity(d)}>{d}</button>
        ))}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={showSubcaps} onChange={(e) => onShowSubcaps(e.target.checked)} />
        Show sub-capabilities
      </label>
    </div>
  </>
);

// ---- Detail panel ----
const DetailPanel: React.FC<{
  cap: FlatCapability;
  apps: any[];
  inits: any[];
  onClose: () => void;
  onJumpMap: () => void;
  onJump: (v: ViewName) => void;
}> = ({ cap, apps, inits, onClose, onJumpMap, onJump }) => {
  const psB = psBucket(cap.ps);
  const subsSorted = [...cap.subCapabilities].sort((a, b) => (b.ps || 0) - (a.ps || 0));

  let rec = 'Maintain — monitor pain & importance.';
  let recColor = '#6e6e73';
  if (cap.tier === 'Commodity' && cap.gap <= 1) { rec = 'Outsource or standardize — commodity capability at acceptable maturity.'; recColor = '#6e6e73'; }
  else if (cap.tier === 'Differentiating' && cap.gap >= 2) { rec = 'INVEST — differentiating capability with material gap.'; recColor = '#95B925'; }
  else if (cap.tier === 'Core' && cap.pain >= 4) { rec = 'Fix the pain — modernize this core capability.'; recColor = '#b8421a'; }
  else if (cap.maturity >= 4 && cap.gap === 0) { rec = 'Defend & extend — at target maturity.'; recColor = '#1d4677'; }

  return (
    <>
      <button className="dp-close" aria-label="Close" onClick={onClose}>×</button>
      <div className="dp-head">
        <div className="crumb">{cap.domain}</div>
        <h2>{cap.name}</h2>
        <div className="purpose">{cap.purpose}</div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span className={`tier-chip tier-${cap.tier}`}>{cap.tier}</span>
          <span className="score-pill"><span className="lbl">Owner</span> {ownerName(cap.owner)}</span>
          <span className="score-pill"><span className="lbl">Annual Spend</span> {money(cap.spend)}</span>
        </div>
        <div className="dp-jumps">
          <button className="jump" onClick={onJumpMap}>Show on map →</button>
          <button className="jump" onClick={() => onJump('gap')}>Gap context →</button>
          {inits.length > 0 && <button className="jump" onClick={() => onJump('portfolio')}>{inits.length} initiative{inits.length > 1 ? 's' : ''} →</button>}
          <button className="jump" onClick={() => onJump('owners')}>Owner's portfolio →</button>
        </div>
      </div>
      <div className="dp-body">
        <div className="dp-section">
          <h4>Scores</h4>
          <div className="score-grid">
            <div className="sc"><div className="lbl">Maturity</div><div className="val">{cap.maturity}</div><div className="scale">/5 current</div></div>
            <div className="sc"><div className="lbl">Pain</div><div className="val">{cap.pain}</div><div className="scale">/5</div></div>
            <div className="sc"><div className="lbl">Importance</div><div className="val">{cap.importance}</div><div className="scale">/5</div></div>
            <div className="sc"><div className="lbl">Priority</div><div className="val" style={{ color: psB >= 4 ? '#b8421a' : 'var(--ink)' }}>{cap.ps}</div><div className="scale">S × P × Gap</div></div>
          </div>
          <div className="gap-bar" style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
              <span><strong>Current {cap.maturity}</strong> → Target {cap.target}</span>
              <span style={{ color: cap.gap >= 2 ? '#b8421a' : 'var(--muted)' }}>{cap.gap === 0 ? 'At target' : `Gap +${cap.gap}`}</span>
            </div>
            <div className="track">
              {[1, 2, 3, 4, 5].map((i) => <div key={i} className={`seg ${i <= cap.maturity ? 'current' : i <= cap.target ? 'target' : ''}`} />)}
            </div>
            <div className="labels"><span>Ad hoc</span><span>Optimized</span></div>
          </div>
        </div>

        <div className="dp-section">
          <h4>Recommendation</h4>
          <div className="rec-box" style={{ borderLeft: `4px solid ${recColor}` }}>{rec}</div>
        </div>

        <div className="dp-section">
          <h4>Sub-capabilities ({subsSorted.length})</h4>
          <div className="sub-list">
            {subsSorted.map((s) => (
              <div className="sub-row" key={s.id} data-bucket={psBucket(s.ps)}>
                <div>
                  <div className="sn">{s.name}</div>
                  <div className="sp">{s.purpose}</div>
                </div>
                <span className="score-pill"><span className="lbl">M</span>{s.maturity}</span>
                <span className="score-pill"><span className="lbl">P</span>{s.pain}</span>
                <span className="score-pill"><span className="lbl">S</span>{s.importance}</span>
                <span className="ps">PS {s.ps}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="dp-section">
          <h4>Supporting applications ({apps.length})</h4>
          {apps.length ? (
            <div className="app-list">
              {apps.map((a) => (
                <div className="app-row" key={a.id}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{a.name}</div>
                    <div className="vendor">{a.type} · {a.vendor}</div>
                  </div>
                  <span className="cost">{money(a.cost)}</span>
                  <span className={`status status-${a.status}`}>{a.status}</span>
                </div>
              ))}
            </div>
          ) : <div style={{ color: 'var(--muted)', fontSize: 12 }}>No applications mapped.</div>}
        </div>

        <div className="dp-section">
          <h4>Active &amp; proposed initiatives ({inits.length})</h4>
          {inits.length ? (
            <div className="init-list">
              {inits.map((i) => (
                <div className="init-row" key={i.id}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{i.name}</div>
                    <div className="vendor">{i.horizon} · {i.owner} · {money(i.cost)} · <span className={`confidence confidence-${i.confidence}`}>{i.confidence} confidence</span></div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}><strong style={{ color: 'var(--ink)' }}>Outcome:</strong> {i.outcome}</div>
                  </div>
                  <span />
                  <span className={`status status-${i.status === 'Proposed' ? 'tolerate' : i.status === 'Approved' ? 'invest' : 'strategic'}`}>{i.status}</span>
                </div>
              ))}
            </div>
          ) : <div style={{ color: 'var(--muted)', fontSize: 12 }}>No active initiatives.</div>}
        </div>
      </div>
    </>
  );
};

export default Beacon;
